<script lang="ts">
  import { onMount } from "svelte"
  import ImageCropper from "$app/components/ImageCropper.svelte"
  import { toast, xhr } from "$app/utils"
  import { onboardingStore } from "./onboarding.svelte"
  import { appStore } from "$app/store.svelte"
  import Navigation from "./Navigation.svelte"
  import PrevNextSkipButtons from "./components/PrevNextSkipButtons.svelte"
  import addPhoto from "@images/onboarding/add-a-photo.png"
  import bg from "@images/onboarding/bg-eclipse.jpg"
  import img from "@images/onboarding/your-profile.png"

  const USER_FIELDS = ["name", "title", "birthdate"]
  let form = {
    name: appStore.current_user.name,
    title: appStore.current_user.title,
    birthdate: appStore.current_user.birthdate
  }
  let errors: any = $state({})
  let avatar: any = $state(null),
    avatarTmp: any = $state(null),
    fileInput: HTMLInputElement = $state(null),
    fileImage: any = $state(null),
    avatarChanged = $state(false)

  let openImgCropper = $state(false)

  const arr = appStore.end_of_year.toString().split("-")
  const maxYear = `${+arr[0] - 13}-${arr[1]}-${arr[2]}`

  onboardingStore.completed = true

  async function updateUser(): Promise<void> {
    errors = {}
    onboardingStore.saving = true

    let formData = new FormData()
    USER_FIELDS.forEach((fld) => formData.append(`user[${fld}]`, form[fld]))

    if (avatarChanged) {
      formData.append("user[avatar]", fileImage)
    }
    formData.append(
      `plan[onboarding_step]`,
      onboardingStore.current_onboarding_step
    )

    try {
      const resp = await xhr.patch(
        `/plan_onboarding/${appStore.plan.id}.json`,
        formData
      )

      appStore.plan.onboarding_steps = resp.data.plan.onboarding_steps
      appStore.current_user = resp.data.user
      onboardingStore.current_onboarding_step = onboardingStore.nextStepName
    } catch (e) {
      toast("There was an error updating your settings", "error")
      if (e.response) {
        errors = { ...errors, ...e.response.data.errors }
      }
    } finally {
      onboardingStore.saving = false
    }
  }

  const onFileSelected = (e) => {
    fileImage = e.target.files[0]
    let reader = new FileReader()
    reader.readAsDataURL(fileImage)
    reader.onload = (e) => {
      avatarTmp = e.target.result

      openImgCropper = true
      avatarChanged = true
    }
  }

  onMount(() => {
    if (appStore.current_user.has_avatar) {
      avatar = appStore.current_user.avatar_thumb_url
      if (avatar) {
        const name = avatar.split("/").pop().split("#")[0].split("?")[0]
        if (name.match(/\.(jpg|jpeg)$/i)) {
          fileImage = { type: "image/jpeg", name }
        } else {
          fileImage = { type: "image/png", name }
        }
      }
    }

    onboardingStore.setBrowserHistory({}, "your_profile")
  })
</script>

<div class="mx-auto px-2 lg:px-6 pt-4 w-full pb-8 lg:pb-20 lg:text-left">
  <div class="px-4 w-full lg:w-1/2 xl:pr-16">
    <Navigation step="your_profile" on:updateStep />

    <h2 class="mt-12 text-5xl font-bold text-left text-white">
      Set Up Your Profile
    </h2>

    <div class="space-y-8 mt-6 md:mt-8 max-w-xl">
      <div>
        <div class="flex justify-start items-center">
          <button
            class="h-24 w-24 rounded-full bg-lwp-pale-blue hover:border-blue-500 hover:cursor-pointer flex justify-center items-center border-4 border-white overflow-hidden"
            onclick={() => {
              fileInput.click()
            }}
          >
            {#if appStore.current_user.has_avatar}
              <img
                src={avatar}
                id="user-avatar"
                alt="Member avatar"
                class="w-24 h-24 rounded-full"
                data-name="avatar-image"
              />
            {:else}
              <img
                src={addPhoto}
                alt="Select Avatar"
                class="w-16 h-16 bg-lwp-pale-blue"
                data-name="avatar-image"
              />
            {/if}
          </button>
          <div class="ml-4 justify-center">
            <button
              class="relative cursor-pointer text-lg font-medium text-white hover:text-indigo-200 hover:underline focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
              onclick={() => {
                fileInput.click()
              }}
            >
              <span>Upload your profile picture</span>
            </button>
          </div>
          {#if avatarChanged}
            <ImageCropper
              image={avatarTmp}
              open={openImgCropper}
              file={fileImage}
              cropShape="round"
              cropperClose={() => {
                openImgCropper = false
              }}
              imageCropped={(data) => {
                avatar = data.image
                fileImage = data.file
                appStore.current_user.has_avatar = true
              }}
            />
          {/if}
          <input
            type="file"
            bind:this={fileInput}
            class="hidden"
            onchange={onFileSelected}
            accept=".jpg, .jpeg, .png"
          />
        </div>
        <div class="mt-6 grid grid-cols-1 gap-y-4">
          <div class="max-w-md">
            <label for="name" class="text-white text-lg"> Full Name </label>
            <div class="mt-2 input-cont" class:error={errors.name}>
              <input
                type="text"
                data-name="name"
                bind:value={form.name}
                class="input input-lg"
              />
            </div>
          </div>
          <div class="max-w-md">
            <label for="title" class="text-white text-lg"> Title </label>
            <div class="mt-2 input-cont" class:error={errors.title}>
              <input
                type="text"
                data-name="title"
                bind:value={form.title}
                class="input input-lg"
              />
              <div class="msg">{errors.title}</div>
            </div>
          </div>
          <div class="max-w-xs">
            <label for="email" class="text-white text-lg"> Birthday </label>
            <div class="mt-2 input-cont" class:error={errors.birthdate}>
              <input
                type="date"
                data-name="birthdate"
                max={maxYear}
                class="input placeholder-text-black p-2 border border-gray-300 w-full input-lg"
                bind:value={form.birthdate}
              />
              <div class="msg">{errors.birthdate}</div>
            </div>
          </div>
        </div>
      </div>

      <PrevNextSkipButtons
        css="justify-evenly"
        nextStepFn={updateUser}
        showSkip={false}
      />
    </div>
  </div>
</div>

<div
  class="relative w-full hidden lg:inline lg:absolute lg:inset-y-0 lg:-right-0 md:w-1/3 lg:w-1/2 lg:h-full bg-lwp-light-grey bg-right-bottom bg-no-repeat bg-contain"
  style="background-image: url({bg})"
>
  <img
    src={img}
    alt="Your Profile"
    class="absolute inset-0 lg:-inset-x-8 lg:inset-y-6 w-11/12 object-fill"
  />
</div>
